import useSearchView from "@hooks/useSearchView";
import {IssueListTypes, Routes, SalesListTypes} from "@lib/constants";
import {getWordpressVars} from "@lib/wordpressVars";
import AccessTokens from "@mwPages/access-tokens";
import CollectorContent from "@mwPages/collector-content";
import CollectorContentSearch from "@mwPages/collector-content/CollectorContentSearch";
import CollectorContentViewAll from "@mwPages/collector-content/CollectorContentViewAll";
import HomePage from "@mwPages/homepage";
import {SignUp} from "@mwPages/index";
import MembershipPlans from "@mwPages/membership-plans";
import MyCollection from "@mwPages/my-collection";
import MyCollectionBoxIssues from "@mwPages/my-collection/boxes/MyCollectionBoxIssues";
import MyCollectionSeriesIssues from "@mwPages/my-collection/series/MyCollectionSeriesIssues";
import NewRelease from "@mwPages/new-release";
import RestrictedAccess from "@mwPages/restriced-access";
import Search from "@mwPages/search";
import {Article as ArticlePage} from "@mwPages/template";
import WeeklyTopTenPage from "@mwPages/weekly-top-ten";
import {Home as PartnerPortalHome, Profile as PartnerPortalProfile,} from "@ppPages/index";
import {useEffect, useMemo, useState} from "react";
import {useLocation, useSearchParams} from "react-router-dom";
import Error404 from "@partials/404";
import Archive from "@mwPosts/archive/Archive";
import {Article as ArticlePost} from "@mwPosts/template/article";
import {Report} from "@mwPosts/template/report";
import SalesList from "@mwPages/sales-lists";
import IssuesList from "@mwPages/issues-lists";
import DescriptorList from "@mwPages/descriptor-lists";
import SeriesPage from "@mwPages/series";
import IssuesPage from "@mwPages/issues";

const AppRoutes = () => {
    const { view } = useSearchView();
    const { pathname } = useLocation();
    const [searchParams] = useSearchParams();


    const [pId] = useState(String(searchParams.has("p")
        ? searchParams.get("p")
        : false)
    );

    const [isPreview] = useState(Boolean(searchParams.has("preview")
        ? searchParams.get("preview")
        : false)
    );

    const [currentView, setCurrentView] = useState(<div className="loading-screen">Loading...</div>);
    const routes = useMemo(() => {

        const components: any = {
            "post/archive": Archive,
            "post/single/comic-report": Report,
            "post/single/article": ArticlePost,
            "post/template/article": ArticlePost,
            "post/custom/article": ArticlePost,
            "post/custom/report": Report,
            "page/custom/article": ArticlePage,
            "404": Error404,
        };

        const slug = getWordpressVars().slug;
        const template = getWordpressVars().template;
        const DyntamicPage = components[template] ?? components['404'];
        const seriesRoute = (('/'+slug).indexOf(Routes.SERIES+'/') === 0) ? '/'+slug : '{{NOT_A_SERIES}}';

        return [
            { view: Routes.ROOT, component: <HomePage /> },
            { view: Routes.PARTNER_PORTAL, component: <PartnerPortalHome /> },
            { view: Routes.PARTNER_PORTAL_PROFILE, component: <PartnerPortalProfile /> },
            { view: Routes.DYNAMIC_PAGE, component: <DyntamicPage /> },
            { view: Routes.TOKEN, component: <AccessTokens /> },
            { view: Routes.NEW_RELEASES, component: <NewRelease /> },
            { view: Routes.MY_COLLECTION, component: <MyCollection /> },
            { view: Routes.SEARCH, component: <Search /> },
            { view: Routes.WEEKLY_MOVERS, component: <SalesList listType={SalesListTypes.WEEKLY_MOVERS} /> },
            { view: Routes.DAILY_HOT, component: <SalesList listType={SalesListTypes.DAILY_HOT} /> },
            { view: Routes.DAILY_SHAKERS, component: <SalesList listType={SalesListTypes.DAILY_SHAKERS} /> },
            { view: Routes.KEY_ISSUES, component: <IssuesList listType={IssueListTypes.KEY_ISSUES}  /> },
            { view: Routes.RARE_ISSUES, component: <IssuesList listType={IssueListTypes.RARE_ISSUES}  /> },
            { view: Routes.TOP_VARIANTS, component: <IssuesList listType={IssueListTypes.TOP_VARIANTS}  /> },
            { view: Routes.WEEKLY_TOP_TEN, component: <WeeklyTopTenPage /> },
            { view: Routes.BOX_ISSUES, component: <MyCollectionBoxIssues /> },
            { view: Routes.SERIES_ISSUES, component: <MyCollectionSeriesIssues /> },
            { view: Routes.COLLECTOR_CONTENT, component: <CollectorContent /> },
            { view: Routes.COLLECTOR_CONTENT_SEARCH, component: <CollectorContentSearch /> },
            { view: Routes.MEMBERSHIP_PLANS_PAGE, component: <MembershipPlans /> },
            { view: Routes.SIGN_UP, component: <SignUp /> },
            { view: Routes.RESTRICTED_ACCESS, component: <RestrictedAccess /> },
            { view: seriesRoute, component: <SeriesPage /> },
            { view: Routes.DESCRIPTOR_LIST, component: <DescriptorList /> },
            { view: Routes.ISSUES, component: <IssuesPage /> },
            { view: `${Routes.COLLECTOR_CONTENT}/${slug}`, component: <CollectorContentViewAll /> },
            { view: `/${slug}`, component: <DyntamicPage /> },
        ];
    }, [])

    useEffect(() => {
        let selected = <Error404 />;

        if (view && view.length > 0 && routes.find(item => item.view === view)) {
            const newView = routes.find(item => item.view === view);
            if (newView) selected = newView.component;
        } else {
            let pathView = pathname;

            //Get view with unmodified path
            let newView = routes.find(item => item.view === pathView);
            //Remove trailing slash
            if (pathname.split('').pop() === '/') pathView = pathname.slice(0, pathname.length - 1);
            //Try again with removed trailing slash
            if (!newView) newView = routes.find(item => item.view === pathView);

            // For Draft Preview: When a post is in a draft state in WordPress, it changes the structure of the URL.
            // Instead of looking for the slug, I check for two query parameters: 'p' and 'preview'.
            // This should allow the route to resolve now and let writers preview posts.
            // ex: http://covrprice.test/?p={post_id}&preview=true
            if (pId && isPreview && pathname === Routes.ROOT) {
                newView = routes.find(item => item.view === Routes.DYNAMIC_PAGE);
            }

            //If newView is set, select component.
            if (newView) selected = newView.component;
        }

        setCurrentView(selected);

    }, [view, pathname, routes, pId, isPreview]);

    return (
        <div >
            {currentView}
        </div>
    );
}

export default AppRoutes